/**
 * ScreenPal — screen recorder and video editor (formerly Screencast-O-Matic)
 *
 * Exposes one web-accessible-resource entry to every origin with no `use_dynamic_url`,
 * listing its `app/*.html` UI pages — editor, tooltips, popup menus, the `som_rec_controls`
 * recorder overlay and more. There are no image WAR, so detection is by fetching a page.
 *
 * `app/som_rec_controls.html` is the primary signal: a distinctive, vendor-named page
 * (`som` for the extension's Screencast-O-Matic origin) exposed at a fixed URL,
 * corroborated by two more named pages. All resolve only when the extension is installed.
 *
 * Manifest verified against version 3.1.8.
 */

import { probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'eefedolmcildfckjamddopaplfiiankl';

export const screenpal: ExtensionDefinition = {
  id: ID,
  name: 'ScreenPal - Screen Recorder & Video Editor',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 90_000,
  notes:
    'One static WAR on every origin exposing its app/*.html pages with no dynamic URL. ' +
    'No image resources, so detection is a fetch of a named page — conclusive.',
  signals: [
    {
      id: 'war-som-rec-controls-html',
      kind: 'web-accessible-resource',
      description:
        'Fetch app/som_rec_controls.html — a vendor-named (som) page exposed to every ' +
        'origin at a fixed URL.',
      weight: 1,
      run: () => probeFetch(ID, 'app/som_rec_controls.html'),
    },
    {
      id: 'war-popup-menu-launcher-html',
      kind: 'web-accessible-resource',
      description: 'Fetch app/popup_menu_launcher.html — the launcher popup page, an independent path.',
      weight: 0.5,
      run: () => probeFetch(ID, 'app/popup_menu_launcher.html'),
    },
    {
      id: 'war-tooltip-choose-html',
      kind: 'web-accessible-resource',
      description:
        'Fetch app/tooltip_choose_what_to_record.html — a distinctively named tooltip page, a third path.',
      weight: 0.5,
      run: () => probeFetch(ID, 'app/tooltip_choose_what_to_record.html'),
    },
  ],
};
